import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Trophy, 
  Users, 
  Calendar, 
  MapPin, 
  DollarSign,
  Award,
  Zap,
  Image,
  Loader2,
  Search,
  UsersRound,
  X,
  Upload,
  ClipboardList,
  CreditCard,
  Lock,
  Eye,
  Edit
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sport formats data
const SPORTS = [
  { id: 'table_tennis', name: 'Table Tennis', icon: '🏓', formats: ['singles', 'doubles'] },
  { id: 'badminton', name: 'Badminton', icon: '🏸', formats: ['singles', 'doubles', 'mixed_doubles'] },
  { id: 'tennis', name: 'Tennis', icon: '🎾', formats: ['singles', 'doubles', 'mixed_doubles'] },
  { id: 'cricket', name: 'Cricket', icon: '🏏', formats: ['double_wicket', '5_side', '11_side'] },
  { id: 'volleyball', name: 'Volleyball', icon: '🏐', formats: ['6_side', '4_side'] },
  { id: 'basketball', name: 'Basketball', icon: '🏀', formats: ['5v5', '3v3'] },
  { id: 'crazy_hide_seek', name: 'Crazy Hide & Seek', icon: '🙈', formats: ['team', 'individual'], aliases: ['hide_seek'] },
];

// Helper to match sport by id or alias
const matchSport = (sportValue) => {
  if (!sportValue) return null;
  return SPORTS.find(s => s.id === sportValue || s.aliases?.includes(sportValue));
};

// Materials checklist by sport
const MATERIALS_CHECKLIST = {
  table_tennis: {
    essential: [
      { id: 'tt_table', name: 'Table Tennis Table', icon: '🏓' },
      { id: 'tt_net', name: 'Table Tennis Net', icon: '🥅' },
      { id: 'tt_paddles', name: 'Paddles (2-4)', icon: '🏓' },
      { id: 'tt_balls', name: 'Table Tennis Balls (6-12)', icon: '⚪' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'towels', name: 'Towels', icon: '🧻' },
      { id: 'scoreboard', name: 'Scoreboard', icon: '📊' },
    ],
  },
  badminton: {
    essential: [
      { id: 'bd_net', name: 'Badminton Net', icon: '🥅' },
      { id: 'bd_rackets', name: 'Rackets (2-4)', icon: '🏸' },
      { id: 'bd_shuttles', name: 'Shuttlecocks (12-24)', icon: '🏸' },
      { id: 'bd_posts', name: 'Net Posts', icon: '🔩' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'towels', name: 'Towels', icon: '🧻' },
      { id: 'court_markers', name: 'Court Line Markers', icon: '📏' },
    ],
  },
  tennis: {
    essential: [
      { id: 'tn_net', name: 'Tennis Net', icon: '🥅' },
      { id: 'tn_rackets', name: 'Tennis Rackets (2-4)', icon: '🎾' },
      { id: 'tn_balls', name: 'Tennis Balls (6-12)', icon: '🎾' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'ball_hopper', name: 'Ball Hopper', icon: '🧺' },
      { id: 'scoreboard', name: 'Scoreboard', icon: '📊' },
    ],
  },
  cricket: {
    essential: [
      { id: 'cr_bat', name: 'Cricket Bat', icon: '🏏' },
      { id: 'cr_ball', name: 'Cricket Ball', icon: '🔴' },
      { id: 'cr_stumps', name: 'Stumps & Bails', icon: '🎯' },
      { id: 'cr_pads', name: 'Batting Pads', icon: '🦵' },
      { id: 'cr_gloves', name: 'Batting Gloves', icon: '🧤' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'helmet', name: 'Helmet', icon: '⛑️' },
      { id: 'boundary_flags', name: 'Boundary Flags', icon: '🚩' },
    ],
  },
  volleyball: {
    essential: [
      { id: 'vb_net', name: 'Volleyball Net', icon: '🥅' },
      { id: 'vb_ball', name: 'Volleyball', icon: '🏐' },
      { id: 'vb_posts', name: 'Net Posts', icon: '🔩' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'scoreboard', name: 'Scoreboard', icon: '📊' },
      { id: 'knee_pads', name: 'Knee Pads', icon: '🦵' },
    ],
  },
  basketball: {
    essential: [
      { id: 'bb_ball', name: 'Basketball', icon: '🏀' },
      { id: 'bb_hoop', name: 'Basketball Hoop', icon: '🏀' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'scoreboard', name: 'Scoreboard', icon: '📊' },
      { id: 'whistle', name: 'Referee Whistle', icon: '📣' },
    ],
  },
  crazy_hide_seek: {
    essential: [
      { id: 'hs_blindfolds', name: 'Blindfolds', icon: '👀' },
      { id: 'hs_timer', name: 'Timer/Stopwatch', icon: '⏱️' },
      { id: 'hs_boundary', name: 'Boundary Markers', icon: '🚧' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'walkie_talkie', name: 'Walkie Talkies', icon: '📻' },
      { id: 'flashlights', name: 'Flashlights', icon: '🔦' },
    ],
  },
};

const SCOPES = [
  { id: 'group', name: 'Group', description: 'Only group members can participate' },
  { id: 'local', name: 'Local', description: 'Open to local participants' },
  { id: 'state', name: 'State', description: 'State-level tournament' },
  { id: 'national', name: 'National', description: 'National-level tournament' },
  { id: 'private', name: 'Private', description: 'Invite-only tournament' },
];

const TOURNAMENT_TYPES = [
  { id: 'knockout', name: 'Knockout', description: 'Single elimination' },
  { id: 'round_robin', name: 'Round Robin', description: 'Everyone plays everyone' },
  { id: 'league', name: 'League', description: 'Points-based league' },
  { id: 'swiss', name: 'Swiss', description: 'Swiss system pairing' },
];

const PAYMENT_TYPES = [
  { id: 'cash', name: 'Cash' },
  { id: 'upi', name: 'UPI' },
  { id: 'bank_transfer', name: 'Bank Transfer' },
  { id: 'cheque', name: 'Cheque' },
  { id: 'neft', name: 'NEFT' },
  { id: 'rtgs', name: 'RTGS' },
];

const STEPS = [
  { id: 1, title: 'Sport Format', icon: Trophy, editLocked: true },
  { id: 2, title: 'Challenge Details', icon: Users, editLocked: true },
  { id: 3, title: 'Event Dates', icon: Calendar, editLocked: false },
  { id: 4, title: 'Tournament Type', icon: Award, editLocked: false },
  { id: 5, title: 'Scoring Rules', icon: Zap, editLocked: false },
  { id: 6, title: 'Venue', icon: MapPin, editLocked: false },
  { id: 7, title: 'Entry Fee', icon: DollarSign, editLocked: true },
  { id: 8, title: 'Prizes', icon: Award, editLocked: false },
  { id: 9, title: 'Banner', icon: Image, editLocked: false },
  { id: 10, title: 'Checklist', icon: ClipboardList, editLocked: false },
  { id: 11, title: 'Payment Details', icon: CreditCard, editLocked: false },
  { id: 12, title: 'Summary', icon: Check, editLocked: false },
];

export default function EventDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'view'; // 'view' or 'edit'
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [eventData, setEventData] = useState(null);
  const bannerInputRef = useRef(null);
  
  // Form data
  const [formData, setFormData] = useState({
    sport: '',
    sport_format: '',
    format_type: 'singles',
    name: '',
    description: '',
    scope: 'group',
    group_id: '',
    group_name: '',
    min_teams: 2,
    max_teams: 32,
    event_start: '',
    event_end: '',
    registration_start: '',
    registration_end: '',
    tournament_type: 'knockout',
    sets_to_win: 1,
    points_per_set: 21,
    deuce_enabled: true,
    max_points_per_set: 30,
    venue_name: '',
    venue_address: '',
    city: '',
    state: '',
    country: 'India',
    is_free: true,
    entry_fee: 0,
    entry_fee_currency: 'INR',
    registration_processing_fee: 0,
    final_fees_total: 0,
    rewards_description: '',
    prizes: [
      { position: 1, title: '1st Place', prize: '' },
      { position: 2, title: '2nd Place', prize: '' },
      { position: 3, title: '3rd Place', prize: '' },
    ],
    banner_image: '',
    banner_file: null,
    logo_url: '',
    materials_checklist: [],
    customer_name: '',
    customer_address: '',
    customer_vendor_code: '',
    customer_gst_no: '',
    customer_po_no: '',
    po_date: '',
    po_amount: 0,
    advance_payment_amount: 0,
    advance_payment_type: '',
    final_payment_amount: 0,
    final_payment_type: '',
    invoice_no: '',
    invoice_date: '',
    other_details: '',
  });

  // Fetch event data
  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/events`);
        const events = response.data.events || [];
        const event = events.find(e => e.id === id);
        
        if (event) {
          setEventData(event);
          setFormData(prev => ({
            ...prev,
            ...event,
            prizes: event.prizes?.length > 0 ? event.prizes : prev.prizes,
            materials_checklist: Array.isArray(event.materials_checklist) ? event.materials_checklist : [],
          }));
        } else {
          toast.error('Event not found');
          navigate('/events');
        }
      } catch (error) {
        toast.error('Failed to load event');
        navigate('/events');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchEvent();
    }
  }, [id, navigate]);

  const handleChange = (field, value) => {
    if (isViewMode) return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePrizeChange = (index, value) => {
    if (isViewMode) return;
    const newPrizes = [...formData.prizes];
    newPrizes[index].prize = value;
    setFormData(prev => ({ ...prev, prizes: newPrizes }));
  };

  const handleChecklistToggle = (itemId) => {
    if (isViewMode) return;
    setFormData(prev => {
      const currentChecklist = prev.materials_checklist || [];
      if (currentChecklist.includes(itemId)) {
        return { ...prev, materials_checklist: currentChecklist.filter(id => id !== itemId) };
      } else {
        return { ...prev, materials_checklist: [...currentChecklist, itemId] };
      }
    });
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, WEBP, or GIF)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }
    
    setUploadingBanner(true);
    try {
      // Create preview URL for immediate display
      const previewUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        banner_image: previewUrl,
        banner_file: file,
      }));
      toast.success('Banner image selected');
    } catch (error) {
      toast.error('Failed to process image');
    } finally {
      setUploadingBanner(false);
    }
  };

  const isStepLocked = (stepId) => {
    if (isViewMode) return true;
    const step = STEPS.find(s => s.id === stepId);
    return step?.editLocked || false;
  };

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let bannerUrl = formData.banner_image;
      
      // If we have a new banner file (local blob), upload it first
      if (formData.banner_file) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', formData.banner_file);
        
        const uploadResponse = await axios.post(`${API_URL}/api/upload/banner`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Use the returned URL
        bannerUrl = `${API_URL}${uploadResponse.data.url}`;
      }
      
      // Only send editable fields
      const updateData = {
        event_start: formData.event_start,
        event_end: formData.event_end,
        registration_start: formData.registration_start,
        registration_end: formData.registration_end,
        tournament_type: formData.tournament_type,
        sets_to_win: formData.sets_to_win,
        points_per_set: formData.points_per_set,
        deuce_enabled: formData.deuce_enabled,
        max_points_per_set: formData.max_points_per_set,
        venue_name: formData.venue_name,
        venue_address: formData.venue_address,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        prizes: formData.prizes,
        banner_image: bannerUrl,
        logo_url: formData.logo_url,
        materials_checklist: formData.materials_checklist,
        customer_name: formData.customer_name,
        customer_address: formData.customer_address,
        customer_vendor_code: formData.customer_vendor_code,
        customer_gst_no: formData.customer_gst_no,
        customer_po_no: formData.customer_po_no,
        po_date: formData.po_date,
        po_amount: formData.po_amount,
        advance_payment_amount: formData.advance_payment_amount,
        advance_payment_type: formData.advance_payment_type,
        final_payment_amount: formData.final_payment_amount,
        final_payment_type: formData.final_payment_type,
        invoice_no: formData.invoice_no,
        invoice_date: formData.invoice_date,
        other_details: formData.other_details,
      };
      
      await axios.put(`${API_URL}/api/admin/challenges/${id}`, updateData);
      toast.success('Event updated successfully');
      navigate('/events');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const selectedSport = matchSport(formData.sport);
  const sportKey = selectedSport?.id || formData.sport;
  const sportChecklist = MATERIALS_CHECKLIST[sportKey] || { essential: [], optional: [] };
  const totalChecklistItems = sportChecklist.essential.length + sportChecklist.optional.length;
  const materialsList = Array.isArray(formData.materials_checklist) ? formData.materials_checklist : [];
  const checkedItems = materialsList.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  const renderLockedOverlay = () => (
    <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px] rounded-lg flex items-center justify-center z-10">
      <div className="text-center">
        <Lock className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
        <p className="text-yellow-500 font-medium">
          {isViewMode ? 'View Only Mode' : 'This section cannot be edited'}
        </p>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto" data-testid="event-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/events')}
            className="text-slate-400"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              {isViewMode ? <Eye className="w-6 h-6 text-blue-400" /> : <Edit className="w-6 h-6 text-green-400" />}
              {isViewMode ? 'View Event' : 'Edit Event'}
            </h1>
            <p className="text-sm text-slate-500">{formData.name}</p>
          </div>
        </div>
        
        {isViewMode && (
          <Button
            onClick={() => navigate(`/events/${id}?mode=edit`)}
            className="bg-green-500 hover:bg-green-600 text-slate-900"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Event
          </Button>
        )}
      </div>

      {/* Mode indicator */}
      <div className={cn(
        "mb-6 p-3 rounded-lg flex items-center gap-2",
        isViewMode ? "bg-blue-500/10 border border-blue-500/30" : "bg-yellow-500/10 border border-yellow-500/30"
      )}>
        {isViewMode ? (
          <>
            <Eye className="w-5 h-5 text-blue-400" />
            <span className="text-blue-400">You are viewing this event in read-only mode</span>
          </>
        ) : (
          <>
            <Lock className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-400">Steps 1, 2 (Sport & Details) and Step 7 (Entry Fee) are locked and cannot be edited</span>
          </>
        )}
      </div>

      {/* Progress Steps */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max pb-2">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isLocked = isStepLocked(step.id);
            
            return (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => setCurrentStep(step.id)}
                  className={cn(
                    "flex items-center gap-1 px-2 py-1.5 rounded-lg transition-all text-xs relative",
                    isActive && "bg-green-500/20 text-green-400 border border-green-500/50",
                    !isActive && "bg-slate-900 text-slate-500",
                    isLocked && !isActive && "opacity-70"
                  )}
                >
                  {isLocked && !isViewMode && (
                    <Lock className="w-2 h-2 absolute -top-1 -right-1 text-yellow-500" />
                  )}
                  <Icon className="w-3 h-3" />
                  <span className="hidden lg:inline">{step.title}</span>
                </button>
                {index < STEPS.length - 1 && (
                  <div className="w-2 h-0.5 mx-0.5 bg-slate-700" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <Card className="bg-slate-900/50 border-slate-800 mb-6">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            {(() => { const Icon = STEPS[currentStep - 1].icon; return <Icon className="w-5 h-5 text-green-400" />; })()}
            {STEPS[currentStep - 1].title}
            {isStepLocked(currentStep) && (
              <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                {isViewMode ? 'View Only' : 'Locked'}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="relative">
          {isStepLocked(currentStep) && !isViewMode && renderLockedOverlay()}
          
          {/* Step 1: Sport Format */}
          {currentStep === 1 && (
            <div className={cn("space-y-6", isStepLocked(1) && "pointer-events-none")}>
              <div>
                <Label className="text-slate-300 mb-3 block">Selected Sport</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {SPORTS.map(sport => {
                    const isSelected = sport.id === formData.sport || sport.aliases?.includes(formData.sport);
                    return (
                      <div
                        key={sport.id}
                        className={cn(
                          "p-4 rounded-lg border text-center",
                          isSelected
                            ? "border-green-500 bg-green-500/20 text-white"
                            : "border-slate-700 bg-slate-800 text-slate-500"
                        )}
                      >
                        <span className="text-3xl block mb-2">{sport.icon}</span>
                        <span className="text-sm font-medium">{sport.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {selectedSport && (
                <div>
                  <Label className="text-slate-300 mb-3 block">Format Type</Label>
                  <div className="flex flex-wrap gap-3">
                    {selectedSport.formats.map(format => (
                      <div
                        key={format}
                        className={cn(
                          "px-4 py-2 rounded-lg border capitalize",
                          formData.format_type === format
                            ? "border-green-500 bg-green-500/20 text-white"
                            : "border-slate-700 bg-slate-800 text-slate-500"
                        )}
                      >
                        {format.replace(/_/g, ' ')}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Challenge Details */}
          {currentStep === 2 && (
            <div className={cn("space-y-6", isStepLocked(2) && "pointer-events-none")}>
              <div>
                <Label className="text-slate-300">Challenge Name</Label>
                <Input
                  value={formData.name}
                  readOnly
                  className="bg-slate-800 border-slate-700 mt-2"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Description</Label>
                <Textarea
                  value={formData.description || 'No description'}
                  readOnly
                  className="bg-slate-800 border-slate-700 mt-2"
                  rows={3}
                />
              </div>
              
              <div>
                <Label className="text-slate-300 mb-3 block">Scope</Label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {SCOPES.map(scope => (
                    <div
                      key={scope.id}
                      className={cn(
                        "p-3 rounded-lg border text-center",
                        formData.scope === scope.id
                          ? "border-green-500 bg-green-500/20 text-white"
                          : "border-slate-700 bg-slate-800 text-slate-500"
                      )}
                    >
                      <span className="text-sm font-medium">{scope.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {formData.group_name && (
                <div>
                  <Label className="text-slate-300">Selected Group</Label>
                  <div className="mt-2 p-3 bg-slate-800 border border-slate-700 rounded-lg flex items-center gap-3">
                    <UsersRound className="w-5 h-5 text-indigo-400" />
                    <span className="text-white">{formData.group_name}</span>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Min Teams</Label>
                  <Input
                    value={formData.min_teams}
                    readOnly
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Max Teams</Label>
                  <Input
                    value={formData.max_teams}
                    readOnly
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Event Dates */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Event Start</Label>
                  <Input
                    type="datetime-local"
                    value={formData.event_start || ''}
                    onChange={(e) => handleChange('event_start', e.target.value)}
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Event End</Label>
                  <Input
                    type="datetime-local"
                    value={formData.event_end || ''}
                    onChange={(e) => handleChange('event_end', e.target.value)}
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Registration Start</Label>
                  <Input
                    type="datetime-local"
                    value={formData.registration_start || ''}
                    onChange={(e) => handleChange('registration_start', e.target.value)}
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Registration End</Label>
                  <Input
                    type="datetime-local"
                    value={formData.registration_end || ''}
                    onChange={(e) => handleChange('registration_end', e.target.value)}
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Tournament Type */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {TOURNAMENT_TYPES.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => handleChange('tournament_type', type.id)}
                    disabled={isViewMode}
                    className={cn(
                      "p-4 rounded-lg border text-left transition-all",
                      formData.tournament_type === type.id
                        ? "border-green-500 bg-green-500/20"
                        : "border-slate-700 bg-slate-800 hover:border-slate-600"
                    )}
                  >
                    <p className="text-white font-medium">{type.name}</p>
                    <p className="text-sm text-slate-400">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Scoring Rules */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Sets to Win</Label>
                  <Select 
                    value={formData.sets_to_win?.toString()} 
                    onValueChange={(v) => handleChange('sets_to_win', parseInt(v))}
                    disabled={isViewMode}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="1">Best of 1</SelectItem>
                      <SelectItem value="2">Best of 3</SelectItem>
                      <SelectItem value="3">Best of 5</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Points Per Set</Label>
                  <Input
                    type="number"
                    value={formData.points_per_set}
                    onChange={(e) => handleChange('points_per_set', parseInt(e.target.value) || 21)}
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                <div>
                  <p className="text-white font-medium">Deuce Enabled</p>
                  <p className="text-sm text-slate-400">Require 2-point lead to win</p>
                </div>
                <Switch
                  checked={formData.deuce_enabled}
                  onCheckedChange={(v) => handleChange('deuce_enabled', v)}
                  disabled={isViewMode}
                />
              </div>
            </div>
          )}

          {/* Step 6: Venue */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-300">Venue Name</Label>
                <Input
                  value={formData.venue_name || ''}
                  onChange={(e) => handleChange('venue_name', e.target.value)}
                  readOnly={isViewMode}
                  className="bg-slate-800 border-slate-700 mt-2"
                />
              </div>
              <div>
                <Label className="text-slate-300">Address</Label>
                <Textarea
                  value={formData.venue_address || ''}
                  onChange={(e) => handleChange('venue_address', e.target.value)}
                  readOnly={isViewMode}
                  className="bg-slate-800 border-slate-700 mt-2"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">City</Label>
                  <Input
                    value={formData.city || ''}
                    onChange={(e) => handleChange('city', e.target.value)}
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">State</Label>
                  <Input
                    value={formData.state || ''}
                    onChange={(e) => handleChange('state', e.target.value)}
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Country</Label>
                  <Input
                    value={formData.country || ''}
                    onChange={(e) => handleChange('country', e.target.value)}
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 7: Entry Fee (Locked in Edit) */}
          {currentStep === 7 && (
            <div className={cn("space-y-6", isStepLocked(7) && "pointer-events-none")}>
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full border-2 border-green-500 flex items-center justify-center">
                    {!formData.is_free && <div className="w-2.5 h-2.5 rounded-full bg-green-500" />}
                  </div>
                  <span className="text-green-400 font-medium">
                    {formData.is_free ? 'Free Entry' : 'Paid Entry'}
                  </span>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Entry Fee (₹)</Label>
                <Input
                  type="number"
                  value={formData.entry_fee}
                  readOnly
                  className="bg-slate-800 border-slate-700 mt-2"
                />
              </div>
              
              {!formData.is_free && formData.entry_fee > 0 && (
                <>
                  <div>
                    <Label className="text-slate-300">Registration Processing Fee</Label>
                    <div className="bg-slate-800 border border-slate-700 rounded-md p-3 mt-2">
                      <span className="text-white">₹ {(formData.registration_processing_fee || 0).toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-green-400 font-medium">Final Fees (Total)</Label>
                    <div className="bg-green-500/20 border border-green-500/50 rounded-md p-3 mt-2">
                      <span className="text-green-400 font-bold text-lg">₹ {(formData.final_fees_total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 8: Prizes */}
          {currentStep === 8 && (
            <div className="space-y-4">
              {formData.prizes.map((prize, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center font-bold",
                    index === 0 && "bg-yellow-500/20 text-yellow-400",
                    index === 1 && "bg-slate-400/20 text-slate-300",
                    index === 2 && "bg-orange-500/20 text-orange-400"
                  )}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <Label className="text-slate-300">{prize.title}</Label>
                    <Input
                      value={prize.prize || ''}
                      onChange={(e) => handlePrizeChange(index, e.target.value)}
                      readOnly={isViewMode}
                      placeholder="e.g., ₹5000 or Trophy + Medal"
                      className="bg-slate-800 border-slate-700 mt-1"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 9: Banner */}
          {currentStep === 9 && (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-300">Banner Image</Label>
                {formData.banner_image && !formData.banner_image.startsWith('blob:') ? (
                  <img 
                    src={formData.banner_image} 
                    alt="Banner" 
                    className="w-full max-w-md h-48 object-cover rounded-lg border border-slate-700 mt-2"
                  />
                ) : formData.banner_image?.startsWith('blob:') ? (
                  <div className="mt-2">
                    <img 
                      src={formData.banner_image} 
                      alt="Banner Preview" 
                      className="w-full max-w-md h-48 object-cover rounded-lg border border-green-500 mt-2"
                    />
                    <p className="text-green-400 text-sm mt-1">New image selected - will be uploaded on save</p>
                  </div>
                ) : (
                  <div 
                    className={cn(
                      "w-full max-w-md h-48 bg-slate-800 border rounded-lg mt-2 flex flex-col items-center justify-center",
                      !isViewMode ? "border-dashed border-slate-600 cursor-pointer hover:border-green-500 transition-colors" : "border-slate-700"
                    )}
                    onClick={() => !isViewMode && bannerInputRef.current?.click()}
                  >
                    {uploadingBanner ? (
                      <Loader2 className="w-8 h-8 animate-spin text-green-500" />
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-slate-500 mb-2" />
                        <span className="text-slate-500">
                          {isViewMode ? 'No banner image' : 'Click to upload banner'}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              
              {!isViewMode && (
                <>
                  {/* File Upload */}
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => bannerInputRef.current?.click()}
                      disabled={uploadingBanner}
                      className="border-slate-700"
                    >
                      {uploadingBanner ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {formData.banner_image ? 'Change Banner' : 'Upload Banner'}
                    </Button>
                    <input
                      ref={bannerInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleBannerUpload}
                      className="hidden"
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      Supported: JPEG, PNG, WEBP, GIF (max 5MB)
                    </p>
                  </div>
                  
                  {/* Or enter URL */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-700" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-slate-900 px-2 text-slate-500">Or enter URL</span>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Banner URL</Label>
                    <Input
                      value={formData.banner_image?.startsWith('blob:') ? '' : (formData.banner_image || '')}
                      onChange={(e) => handleChange('banner_image', e.target.value)}
                      placeholder="https://..."
                      className="bg-slate-800 border-slate-700 mt-2"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 10: Materials Checklist */}
          {currentStep === 10 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Materials Checklist</h3>
                <p className="text-sm text-slate-400 mb-4">Tap items to mark as done</p>
                
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-6">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-green-400">{checkedItems} of {totalChecklistItems} items ready</span>
                  </div>
                </div>
              </div>
              
              {/* Essential Items */}
              <div>
                <h4 className="text-sm font-medium text-white mb-3">Essential Items</h4>
                <div className="space-y-2">
                  {sportChecklist.essential.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleChecklistToggle(item.id)}
                      disabled={isViewMode}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                        materialsList.includes(item.id)
                          ? "border-green-500 bg-green-500/10"
                          : "border-slate-700 bg-slate-800"
                      )}
                    >
                      <div className={cn(
                        "w-6 h-6 rounded border-2 flex items-center justify-center",
                        materialsList.includes(item.id)
                          ? "border-green-500 bg-green-500"
                          : "border-slate-500"
                      )}>
                        {materialsList.includes(item.id) && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-white">{item.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Optional Items */}
              <div>
                <h4 className="text-sm font-medium text-white mb-3">Optional Items</h4>
                <div className="space-y-2">
                  {sportChecklist.optional.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleChecklistToggle(item.id)}
                      disabled={isViewMode}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                        materialsList.includes(item.id)
                          ? "border-green-500 bg-green-500/10"
                          : "border-slate-700 bg-slate-800"
                      )}
                    >
                      <div className={cn(
                        "w-6 h-6 rounded border-2 flex items-center justify-center",
                        materialsList.includes(item.id)
                          ? "border-green-500 bg-green-500"
                          : "border-slate-500"
                      )}>
                        {materialsList.includes(item.id) && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-white">{item.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 11: Payment Details */}
          {currentStep === 11 && (
            <div className="space-y-6">
              <p className="text-sm text-slate-400">All fields are optional - fill only what's needed</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Customer Name</Label>
                  <Input
                    value={formData.customer_name || ''}
                    onChange={(e) => handleChange('customer_name', e.target.value)}
                    placeholder="Client/Customer name"
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Vendor Code</Label>
                  <Input
                    value={formData.customer_vendor_code || ''}
                    onChange={(e) => handleChange('customer_vendor_code', e.target.value)}
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Customer Address</Label>
                <Textarea
                  value={formData.customer_address || ''}
                  onChange={(e) => handleChange('customer_address', e.target.value)}
                  readOnly={isViewMode}
                  className="bg-slate-800 border-slate-700 mt-2"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">GST Number</Label>
                  <Input
                    value={formData.customer_gst_no || ''}
                    onChange={(e) => handleChange('customer_gst_no', e.target.value)}
                    placeholder="e.g., 29ABCDE1234F1Z5"
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">PO Number</Label>
                  <Input
                    value={formData.customer_po_no || ''}
                    onChange={(e) => handleChange('customer_po_no', e.target.value)}
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">PO Date</Label>
                  <Input
                    type="date"
                    value={formData.po_date || ''}
                    onChange={(e) => handleChange('po_date', e.target.value)}
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">PO Amount (₹)</Label>
                  <Input
                    type="number"
                    value={formData.po_amount || ''}
                    onChange={(e) => handleChange('po_amount', parseFloat(e.target.value) || 0)}
                    readOnly={isViewMode}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-white font-medium mb-4">Advance Payment</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Amount (₹)</Label>
                    <Input
                      type="number"
                      value={formData.advance_payment_amount || ''}
                      onChange={(e) => handleChange('advance_payment_amount', parseFloat(e.target.value) || 0)}
                      readOnly={isViewMode}
                      className="bg-slate-800 border-slate-700 mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Payment Type</Label>
                    <Select 
                      value={formData.advance_payment_type || "none"} 
                      onValueChange={(v) => handleChange('advance_payment_type', v === "none" ? "" : v)}
                      disabled={isViewMode}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 mt-2">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="none">Select type</SelectItem>
                        {PAYMENT_TYPES.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-white font-medium mb-4">Final Payment</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Amount (₹)</Label>
                    <Input
                      type="number"
                      value={formData.final_payment_amount || ''}
                      onChange={(e) => handleChange('final_payment_amount', parseFloat(e.target.value) || 0)}
                      readOnly={isViewMode}
                      className="bg-slate-800 border-slate-700 mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Payment Type</Label>
                    <Select 
                      value={formData.final_payment_type || "none"} 
                      onValueChange={(v) => handleChange('final_payment_type', v === "none" ? "" : v)}
                      disabled={isViewMode}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 mt-2">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="none">Select type</SelectItem>
                        {PAYMENT_TYPES.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-white font-medium mb-4">Invoice Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Invoice Number</Label>
                    <Input
                      value={formData.invoice_no || ''}
                      onChange={(e) => handleChange('invoice_no', e.target.value)}
                      readOnly={isViewMode}
                      className="bg-slate-800 border-slate-700 mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Invoice Date</Label>
                    <Input
                      type="date"
                      value={formData.invoice_date || ''}
                      onChange={(e) => handleChange('invoice_date', e.target.value)}
                      readOnly={isViewMode}
                      className="bg-slate-800 border-slate-700 mt-2"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Other Details</Label>
                <Textarea
                  value={formData.other_details || ''}
                  onChange={(e) => handleChange('other_details', e.target.value)}
                  placeholder="Additional details (up to 1000 chars)"
                  maxLength={1000}
                  readOnly={isViewMode}
                  className="bg-slate-800 border-slate-700 mt-2"
                  rows={3}
                />
                <p className="text-xs text-slate-500 mt-1">{formData.other_details?.length || 0}/1000</p>
              </div>
            </div>
          )}

          {/* Step 12: Summary */}
          {currentStep === 12 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Challenge Details</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-400">Name:</span> <span className="text-white">{formData.name}</span></p>
                    <p><span className="text-slate-400">Sport:</span> <span className="text-white">{selectedSport?.name} ({formData.format_type})</span></p>
                    <p><span className="text-slate-400">Scope:</span> <span className="text-white capitalize">{formData.scope}</span></p>
                    {formData.group_name && <p><span className="text-slate-400">Group:</span> <span className="text-white">{formData.group_name}</span></p>}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Entry & Prizes</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-400">Entry Fee:</span> <span className="text-white">{formData.is_free ? 'Free' : `₹${formData.entry_fee}`}</span></p>
                    {formData.prizes.filter(p => p.prize).map(p => (
                      <p key={p.position}><span className="text-slate-400">{p.title}:</span> <span className="text-white">{p.prize}</span></p>
                    ))}
                  </div>
                </div>
              </div>
              
              {formData.venue_name && (
                <div className="p-4 bg-slate-800 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-2">Venue</h3>
                  <p className="text-slate-300">{formData.venue_name}</p>
                  <p className="text-sm text-slate-400">{formData.city}, {formData.state}</p>
                </div>
              )}
              
              <div className="p-4 bg-slate-800 rounded-lg">
                <h3 className="text-sm font-medium text-slate-300 mb-2">Status</h3>
                <span className={cn(
                  "px-3 py-1 rounded-full text-sm font-medium",
                  formData.status === 'draft' && "bg-slate-500/20 text-slate-300",
                  formData.status === 'registration_open' && "bg-green-500/20 text-green-400",
                  formData.status === 'completed' && "bg-blue-500/20 text-blue-400"
                )}>
                  {formData.status || 'Draft'}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 1}
          className="border-slate-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        
        <div className="flex gap-2">
          {currentStep < STEPS.length ? (
            <Button
              onClick={nextStep}
              className="bg-green-500 hover:bg-green-600 text-slate-900"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            isEditMode && (
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-green-500 hover:bg-green-600 text-slate-900"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
